/**
 * Helper utilities for managing syntax data structures and correctness state
 * Supports data transformations and state management across difficulty levels
 */

import { tagInfo, phraseInfo } from '../features/assignment/components/analysis/syntax_analysis/constants';

/**
 * Deep clone object to avoid mutation issues
 * @param {*} obj - Object to clone
 * @returns {*} Deep cloned object
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  if (typeof obj === 'object') {
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
};

/**
 * Update page data with correctness information for Elementary level
 * @param {Object} pageData - Current page data
 * @param {Object} evaluationResult - Evaluation result with correctness
 * @returns {Object} Updated page data with correctness tracking
 */
export const updateElementaryPageWithCorrectness = (pageData, evaluationResult) => {
  const updatedData = deepClone(pageData.data);
  const updatedAnswers = deepClone(pageData.answers);
  
  // Update group elements with correctness information
  updatedData.forEach(block => {
    if (block.type === 'group') {
      const tokenIndices = block.text.map(t => t.index);
      
      // Find matching correctness data
      const matchingCorrectness = Object.values(evaluationResult.correctness).find(c => 
        c.tokenIndices.length === tokenIndices.length &&
        c.tokenIndices.every(idx => tokenIndices.includes(idx))
      );
      
      if (matchingCorrectness) {
        block.isCorrect = matchingCorrectness.isCorrect;
        block.elementId = matchingCorrectness.elementId;
        
        // If correct, mark as submitted
        if (matchingCorrectness.isCorrect) {
          block.isSubmitted = true;
        } else {
          // If incorrect, reset groupType to allow retry
          block.groupType = '';
        }
      }
    }
  });
  
  // Update answers array with correctness
  updatedAnswers.forEach(answer => {
    const matchingCorrectness = Object.values(evaluationResult.correctness).find(c => 
      c.tokenIndices.length === answer.token_indices.length &&
      c.tokenIndices.every(idx => answer.token_indices.includes(idx)) &&
      c.expectedType === answer.type
    );
    
    if (matchingCorrectness) {
      answer.isCorrect = matchingCorrectness.isCorrect;
      answer.studentAnswer = matchingCorrectness.studentType;
      answer.elementId = matchingCorrectness.elementId;
      
      // Track first attempt result for PDF reporting
      if (!answer.hasBeenAttempted) {
        answer.firstAttemptCorrect = matchingCorrectness.isCorrect;
        answer.firstAttemptAnswer = matchingCorrectness.studentType;
        answer.hasBeenAttempted = true;
      }
      
      if (matchingCorrectness.isCorrect) {
        answer.isSubmitted = true;
      }
    }
  });
  
  return {
    ...pageData,
    data: updatedData,
    answers: updatedAnswers,
    correctnessTracking: {
      totalElements: evaluationResult.totalCount,
      correctElements: evaluationResult.correctCount,
      incorrectElements: evaluationResult.totalCount - evaluationResult.correctCount,
      lastSubmitTime: new Date().toISOString(),
      level: 'elementary'
    }
  };
};

/**
 * Update page data with correctness information for Intermediate level
 * @param {Object} pageData - Current page data
 * @param {Object} evaluationResult - Evaluation result with correctness
 * @returns {Object} Updated page data with correctness tracking
 */
export const updateIntermediatePageWithCorrectness = (pageData, evaluationResult) => {
  const updatedData = [];
  const updatedAnswers = deepClone(pageData.answers);
  
  // Process each data element
  pageData.data.forEach(block => {
    if (block.type === 'group') {
      const tokenIndices = block.text.map(t => t.index);
      
      // Find matching correctness data
      const matchingCorrectness = Object.values(evaluationResult.correctness).find(c => 
        c.tokenIndices.length === tokenIndices.length &&
        c.tokenIndices.every(idx => tokenIndices.includes(idx))
      );
      
      if (matchingCorrectness && matchingCorrectness.isCorrect) {
        // Keep as group if correct
        updatedData.push({
          ...block,
          isCorrect: true,
          isSubmitted: true,
          elementId: matchingCorrectness.elementId
        });
      } else {
        // Break back into tokens if incorrect
        updatedData.push(...block.text);
      }
    } else {
      // Keep tokens as is
      updatedData.push(block);
    }
  });
  
  // Update answers array with correctness
  updatedAnswers.forEach(answer => {
    const matchingCorrectness = Object.values(evaluationResult.correctness).find(c => 
      c.tokenIndices.length === answer.token_indices.length &&
      c.tokenIndices.every(idx => answer.token_indices.includes(idx)) &&
      c.expectedType === answer.type
    );
    
    if (matchingCorrectness) {
      answer.isCorrect = matchingCorrectness.isCorrect;
      answer.studentAnswer = matchingCorrectness.studentType;
      answer.elementId = matchingCorrectness.elementId;
      
      // Track first attempt result for PDF reporting
      if (!answer.hasBeenAttempted) {
        answer.firstAttemptCorrect = matchingCorrectness.isCorrect;
        answer.firstAttemptAnswer = matchingCorrectness.studentType;
        answer.hasBeenAttempted = true;
      }
      
      if (matchingCorrectness.isCorrect) {
        answer.isSubmitted = true;
      }
    }
  });
  
  return {
    ...pageData,
    data: updatedData,
    answers: updatedAnswers,
    correctnessTracking: {
      totalElements: evaluationResult.totalCount,
      correctElements: evaluationResult.correctCount,
      incorrectElements: evaluationResult.totalCount - evaluationResult.correctCount,
      lastSubmitTime: new Date().toISOString(),
      level: 'intermediate'
    }
  };
};

/**
 * Update page data with correctness information for Advanced level
 * @param {Object} pageData - Current page data
 * @param {Object} evaluationResult - Evaluation result with correctness
 * @returns {Object} Updated page data with correctness tracking
 */
export const updateAdvancedPageWithCorrectness = (pageData, evaluationResult) => {
  const updatedGroupData = deepClone(pageData.groupData || {});
  const updatedPhraseData = deepClone(pageData.phraseData || {});
  const updatedAnswers = deepClone(pageData.answerData || []);

  // Update group data with correctness
  Object.values(evaluationResult.correctness).forEach(correctnessInfo => {
    if (!correctnessInfo.isCorrect) {
      // Remove incorrect group data to allow retry
      correctnessInfo.tokenIndices.forEach(tokenIndex => {
        if (updatedGroupData[tokenIndex] &&
            updatedGroupData[tokenIndex].groupType === correctnessInfo.expectedType) {
          delete updatedGroupData[tokenIndex];
        }
      });
    } else {
      // Mark correct group data
      correctnessInfo.tokenIndices.forEach(tokenIndex => {
        if (updatedGroupData[tokenIndex]) {
          updatedGroupData[tokenIndex].isCorrect = true;
          updatedGroupData[tokenIndex].elementId = correctnessInfo.elementId;
        }
      });
    }
  });

  const correctPhrases = Object.values(evaluationResult.correctness).filter(
    correctnessInfo =>
      correctnessInfo.isCorrect && phraseInfo[correctnessInfo.expectedType]
  );

  const findCorrectPhrase = (phrase, startIndex, endIndex) =>
    correctPhrases.find(correctnessInfo => {
      const expectedStartIndex = correctnessInfo.tokenIndices[0];
      const expectedEndIndex =
        correctnessInfo.tokenIndices[correctnessInfo.tokenIndices.length - 1];

      return (
        correctnessInfo.expectedType === phrase.type &&
        String(expectedStartIndex) === String(startIndex) &&
        String(expectedEndIndex) === String(endIndex)
      );
    });

  // Phrase selections are stored as a pair at their start and end. Filter the
  // student's actual selections, not the expected coordinates, so an
  // incorrectly ranged modifier is removed and can be selected again.
  Object.keys(updatedPhraseData).forEach(boundaryIndex => {
    const boundary = updatedPhraseData[boundaryIndex];
    const seenStartPhrases = new Set();
    const seenEndPhrases = new Set();

    boundary.start = (boundary.start || []).flatMap(phrase => {
      const matchingCorrectness = findCorrectPhrase(
        phrase,
        boundaryIndex,
        phrase.endIndex
      );
      const phraseKey = `${phrase.type}:${phrase.endIndex}`;

      if (!matchingCorrectness || seenStartPhrases.has(phraseKey)) {
        return [];
      }
      seenStartPhrases.add(phraseKey);

      return [
        {
          ...phrase,
          isCorrect: true,
          elementId: matchingCorrectness.elementId,
        },
      ];
    });

    boundary.end = (boundary.end || []).flatMap(phrase => {
      const matchingCorrectness = findCorrectPhrase(
        phrase,
        phrase.startIndex,
        boundaryIndex
      );
      const phraseKey = `${phrase.type}:${phrase.startIndex}`;

      if (!matchingCorrectness || seenEndPhrases.has(phraseKey)) {
        return [];
      }
      seenEndPhrases.add(phraseKey);

      return [
        {
          ...phrase,
          isCorrect: true,
          elementId: matchingCorrectness.elementId,
        },
      ];
    });

    if (boundary.start.length === 0 && boundary.end.length === 0) {
      delete updatedPhraseData[boundaryIndex];
    }
  });
  
  // Update answer data with correctness
  updatedAnswers.forEach(answer => {
    const matchingCorrectness = Object.values(evaluationResult.correctness).find(c => 
      c.tokenIndices.length === answer.token_indices.length &&
      c.tokenIndices.every(idx => answer.token_indices.includes(idx)) &&
      c.expectedType === answer.type
    );
    
    if (matchingCorrectness) {
      answer.isCorrect = matchingCorrectness.isCorrect;
      answer.studentAnswer = matchingCorrectness.studentType;
      answer.elementId = matchingCorrectness.elementId;
      
      // Track first attempt result for PDF reporting
      if (!answer.hasBeenAttempted) {
        answer.firstAttemptCorrect = matchingCorrectness.isCorrect;
        answer.firstAttemptAnswer = matchingCorrectness.studentType;
        answer.hasBeenAttempted = true;
      }
    }
  });
  
  return {
    ...pageData,
    groupData: updatedGroupData,
    phraseData: updatedPhraseData,
    answerData: updatedAnswers,
    correctnessTracking: {
      totalElements: evaluationResult.totalCount,
      correctElements: evaluationResult.correctCount,
      incorrectElements: evaluationResult.totalCount - evaluationResult.correctCount,
      lastSubmitTime: new Date().toISOString(),
      level: 'advanced'
    }
  };
};

/**
 * Create auto-revealed data structure when max attempts reached
 * @param {Object} pageData - Current page data
 * @param {Array} correctAnswers - Correct answers to reveal
 * @param {string} level - Difficulty level
 * @returns {Object} Updated page data with auto-revealed answers
 */
export const createAutoRevealedPageData = (pageData, correctAnswers, level) => {
  const updatedPageData = deepClone(pageData);
  
  if (level === 'elementary') {
    // Update data blocks with correct groupTypes
    updatedPageData.data.forEach(block => {
      if (block.type === 'group') {
        const tokenIndices = block.text.map(t => t.index);
        const correctAnswer = correctAnswers.find(answer => 
          answer.token_indices.length === tokenIndices.length &&
          answer.token_indices.every(idx => tokenIndices.includes(idx))
        );
        
        if (correctAnswer) {
          block.groupType = correctAnswer.type;
          block.isSubmitted = true;
          block.isCorrect = true;
          block.autoRevealed = true;
        }
      }
    });
    
    // Update answers
    updatedPageData.answers.forEach(answer => {
      answer.isSubmitted = true;
      answer.isCorrect = true;
      answer.autoRevealed = true;
    });
    
  } else if (level === 'intermediate') {
    // Reconstruct groups with correct types
    const newData = [];
    const processedTokens = new Set();
    
    correctAnswers.forEach(answer => {
      if (!processedTokens.has(answer.token_indices[0])) {
        // Find tokens for this answer
        const groupTokens = [];
        answer.token_indices.forEach(tokenIndex => {
          const token = updatedPageData.data.find(item => 
            item.type === 'token' && item.index === tokenIndex
          );
          if (token) {
            groupTokens.push(token);
            processedTokens.add(tokenIndex);
          }
        });
        
        if (groupTokens.length > 0) {
          newData.push({
            type: 'group',
            groupIndex: answer.token_indices[0],
            groupType: answer.type,
            isSubmitted: true,
            isCorrect: true,
            autoRevealed: true,
            text: groupTokens
          });
        }
      }
    });
    
    // Add remaining tokens
    updatedPageData.data.forEach(item => {
      if (item.type === 'token' && !processedTokens.has(item.index)) {
        newData.push(item);
      }
    });
    
    updatedPageData.data = newData;
    
    // Update answers
    updatedPageData.answers.forEach(answer => {
      answer.isSubmitted = true;
      answer.isCorrect = true;
      answer.autoRevealed = true;
    });
    
  } else if (level === 'advanced') {
    // Set correct group and phrase data
    const newGroupData = {};
    const newPhraseData = {};
    
    correctAnswers.forEach(answer => {
      if (answer.type in tagInfo) {
        // Group data
        const groupLabelIndex = Math.floor(answer.token_indices.length / 2);
        answer.token_indices.forEach((tokenIndex, i) => {
          newGroupData[tokenIndex] = {
            groupType: answer.type,
            isGroupEnd: i === answer.token_indices.length - 1,
            isGroupLabel: i === groupLabelIndex,
            isCorrect: true,
            autoRevealed: true
          };
        });
      } else {
        // Phrase data
        const startIndex = answer.token_indices[0];
        const endIndex = answer.token_indices[answer.token_indices.length - 1];
        
        if (!newPhraseData[startIndex]) {
          newPhraseData[startIndex] = { start: [], end: [] };
        }
        if (!newPhraseData[endIndex]) {
          newPhraseData[endIndex] = { start: [], end: [] };
        }
        
        const phraseInfoData = phraseInfo[answer.type];
        
        newPhraseData[startIndex].start.push({
          ...phraseInfoData,
          endIndex,
          isCorrect: true,
          autoRevealed: true
        });
        
        newPhraseData[endIndex].end.push({
          ...phraseInfoData,
          startIndex,
          isCorrect: true,
          autoRevealed: true
        });
      }
    });
    
    updatedPageData.groupData = newGroupData;
    updatedPageData.phraseData = newPhraseData;
    
    // Update answer data
    if (updatedPageData.answerData) {
      updatedPageData.answerData.forEach(answer => {
        answer.isCorrect = true;
        answer.autoRevealed = true;
      });
    }
  }
  
  updatedPageData.isSubmitted = true;
  updatedPageData.autoRevealed = true;
  updatedPageData.correctnessTracking = {
    totalElements: correctAnswers.length,
    correctElements: correctAnswers.length,
    incorrectElements: 0,
    lastSubmitTime: new Date().toISOString(),
    level,
    autoRevealed: true
  };
  
  return updatedPageData;
};

/**
 * Extract incorrect elements for retry system
 * @param {Object} pageData - Page data with correctness information
 * @returns {Array} Array of incorrect elements for retry
 */
export const extractIncorrectElementsForRetry = (pageData) => {
  const incorrectElements = [];
  
  if (pageData.answers) {
    pageData.answers.forEach(answer => {
      if (answer.isCorrect === false) {
        incorrectElements.push({
          type: answer.type,
          token_indices: answer.token_indices,
          elementId: answer.elementId,
          expectedType: answer.type,
          studentAnswer: answer.studentAnswer
        });
      }
    });
  }
  
  return incorrectElements;
};

/**
 * Merge correctness data from multiple pages
 * @param {Array} pageList - Array of page data with correctness
 * @returns {Object} Merged correctness statistics
 */
export const mergeCorrectnessData = (pageList) => {
  let totalElements = 0;
  let correctElements = 0;
  let incorrectElements = 0;
  const allIncorrectElements = [];
  
  pageList.forEach(page => {
    if (page.correctnessTracking) {
      totalElements += page.correctnessTracking.totalElements || 0;
      correctElements += page.correctnessTracking.correctElements || 0;
      incorrectElements += page.correctnessTracking.incorrectElements || 0;
    }
    
    // Collect incorrect elements
    const pageIncorrectElements = extractIncorrectElementsForRetry(page);
    allIncorrectElements.push(...pageIncorrectElements);
  });
  
  return {
    totalElements,
    correctElements,
    incorrectElements,
    allIncorrectElements,
    overallAccuracy: totalElements > 0 ? (correctElements / totalElements) * 100 : 0
  };
};
